import { Suspense, useRef } from "react";
import { useInView } from "framer-motion";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { Float, Environment, Lightformer, Sparkles } from "@react-three/drei";

export function StudioEnv() {
  return (
    <Environment resolution={256} frames={1}>
      <Lightformer intensity={4} rotation-x={Math.PI / 2} position={[0, 5, -9]} scale={[10, 10, 1]} color="#fff6e0" />
      <Lightformer intensity={2.5} rotation-y={Math.PI / 2} position={[-5, 1, -1]} scale={[10, 2, 1]} color="#ffe2a8" />
      <Lightformer intensity={2.5} rotation-y={-Math.PI / 2} position={[10, 1, 0]} scale={[20, 2, 1]} color="#ffffff" />
      <Lightformer form="ring" intensity={3} position={[0, 2, 6]} scale={4} color="#f5d98a" />
      <Lightformer intensity={1.5} rotation-x={-Math.PI / 2} position={[0, -5, 0]} scale={[10, 10, 1]} color="#8a6a1f" />
    </Environment>
  );
}

const SHAPES = [
  { pos: [-0.96, 1.8, -1], size: 0.22, kind: "ico" },
  { pos: [0.94, -1.2, -0.5], size: 0.3, kind: "octa" },
  { pos: [-0.9, -2.4, 0.2], size: 0.16, kind: "ico" },
  { pos: [0.88, 2.6, -1.6], size: 0.18, kind: "octa" },
  { pos: [-0.98, -0.3, -2], size: 0.36, kind: "ring" },
  { pos: [0.97, 1.4, -2.2], size: 0.42, kind: "ring" },
];

function Shape({ pos, size, kind, i }) {
  const ref = useRef();
  const { viewport } = useThree();
  const x = (pos[0] * viewport.width) / 2;
  useFrame((_, d) => {
    if (!ref.current) return;
    ref.current.rotation.x += d * (0.3 + i * 0.05);
    ref.current.rotation.y += d * 0.4;
  });
  return (
    <Float speed={1.4 + i * 0.2} rotationIntensity={0.6} floatIntensity={1.6}>
      <mesh ref={ref} position={[x, pos[1], pos[2]]}>
        {kind === "ico" && <icosahedronGeometry args={[size, 0]} />}
        {kind === "octa" && <octahedronGeometry args={[size, 0]} />}
        {kind === "ring" && <torusGeometry args={[size, 0.03, 16, 80]} />}
        <meshStandardMaterial color="#E2C063" metalness={0.9} roughness={0.28} envMapIntensity={1.6} />
      </mesh>
    </Float>
  );
}

function Rings() {
  const g = useRef();
  useFrame((_, d) => {
    if (!g.current) return;
    g.current.rotation.x += d * 0.12;
    g.current.rotation.y += d * 0.2;
  });
  return (
    <group ref={g}>
      {[2.6, 3.3, 4.1].map((r, i) => (
        <mesh key={r} rotation={[i * 0.75, i * 0.45, 0]}>
          <torusGeometry args={[r, 0.014, 12, 180]} />
          <meshStandardMaterial color="#E9C767" metalness={1} roughness={0.25} />
        </mesh>
      ))}
    </group>
  );
}

export default function GoldDust({ className = "", dark = false, rings = false, shapes = true }) {
  const ref = useRef(null);
  const inView = useInView(ref, { margin: "200px 0px 200px 0px" });
  return (
    <div ref={ref} className={`pointer-events-none ${className}`} aria-hidden="true">
      {inView && (
      <Canvas dpr={[1, 1.25]} camera={{ position: [0, 0, 8], fov: 45 }} gl={{ alpha: true, antialias: true, powerPreference: "high-performance" }}>
        <ambientLight intensity={dark ? 0.4 : 0.9} />
        <directionalLight position={[4, 6, 5]} intensity={2.2} color="#ffe6b0" />
        <Suspense fallback={null}>
          <StudioEnv />
          <Sparkles
            count={dark ? 80 : 60}
            scale={[18, 10, 6]}
            size={dark ? 2.6 : 3.2}
            speed={0.3}
            color={dark ? "#E9C767" : "#B9962B"}
            opacity={dark ? 0.75 : 0.5}
          />
          {shapes && SHAPES.map((s, i) => <Shape key={i} i={i} {...s} />)}
          {rings && <Rings />}
        </Suspense>
      </Canvas>
      )}
    </div>
  );
}
